export interface ControlStep {
  id: string;
  content: string;
}

export interface ControlOwner {
  id: string;
  name: string;
  jobTitle: string;
  background: string;
  email?: string;
}

export interface DependentControl {
  id: string;
  controlId: string;
  controlName: string;
  controlDescription: string;
  designImplementation: string;
  operatingEffectiveness: string;
}

export interface RollforwardInquiry {
  id: string;
  name: string;
  date: string;
}

export interface DesignImplementationStep {
  id: string;
  stepDescription: string;
  natureOfProcedure: string;
  procedureDescription: string;
}

export interface OperatingEffectivenessStep {
  id: string;
  stepDescription: string;
  natureOfProcedure: string;
  procedureDescription: string;
}

export interface RelatedAccount {
  par: string;
  noteLineId: string;
  noteLine: string;
  amount: number;
}

export interface InternalControlData {
  // Control Summary - Control Details
  controlId: string;
  controlName: string;
  workspaceLinked: string[];
  controlDescription: string;
  relatedAccounts?: RelatedAccount[];
  
  // Control Attributes
  typeOfControl: "Direct" | "Indirect" | "GITC";
  nature: "Manual" | "Automated" | "Service Organization Control";
  approach: "Preventive" | "Detective";
  controlTypes: {
    verification: boolean;
    authorizationsApprovals: boolean;
    physicalControlsCounts: boolean;
    controlsOverIUC: boolean;
    reconciliations: boolean;
    controlWithReviewElement: boolean;
    gitcControl: boolean;
  };
  
  // Tailoring Questions
  addressesSignificantRisk: boolean;
  includesReviewElement: boolean;
  
  // Detailed Description
  detailedDescriptionEnabled: boolean;
  steps: ControlStep[];
  iucAssociations: string[]; // Legacy field, kept for backward compatibility
  iucDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  iucTextResponse?: { html: string; plainText: string };
  controlOutputs: ControlStep[]; // Legacy field, kept for backward compatibility
  outputDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  outputTextResponse?: { html: string; plainText: string };
  
  // Evaluate Design & Implementation
  designImplementationSteps: DesignImplementationStep[];
  testOfDesignImplementation: string;
  evidenceDocuments: string[]; // Legacy field, kept for backward compatibility
  evidenceDesignDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  evidenceDesignTextResponse?: { html: string; plainText: string };
  
  // Design Factors
  designFactor1Response: string;
  designFactor2Enabled: boolean;
  controlOwners: ControlOwner[];
  designFactor3Enabled: boolean;
  frequency: string;
  consistency: string;
  designFactor4Enabled: boolean;
  designFactor4Content: string;
  designFactor5Enabled: boolean;
  designFactor5Content: string;
  
  // Dependent Controls
  isDependentOnOtherControls: boolean;
  dependentControls: DependentControl[];
  
  // Effectiveness of Design & Implementation
  isDesignEffective: "Yes" | "No" | "Not Yet Concluded";
  isProperlyImplemented: "Yes" | "No" | "Not Yet Concluded";
  
  // Prior Year Audit Evidence
  usePriorYearEvidence: boolean;
  changesInProcess: string;
  changesSignificant: boolean;
  testedCurrentYear: boolean;
  associatedControls: DependentControl[];
  priorYearDocument: string; // Legacy field, kept for backward compatibility
  priorYearDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  priorYearTextResponse?: { html: string; plainText: string };
  
  // Risk Associated with Control
  riskAssociated: "Higher" | "Not Higher";
  riskBasisConclusion: string;
  
  // Operating Effectiveness
  timingOfProcedure: "Apportion" | "Conclude at Interim";
  timingDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  timingTextResponse?: { html: string; plainText: string };
  frequencyOfPerformance: string;
  riskOfMaterialMisstatement: string;
  sampleSize: string;
  planningForDeviations: boolean;
  deviationDescription: string;
  deviationRationale: string;
  operatingSteps: OperatingEffectivenessStep[];
  isOperatingEffective: "Yes" | "No" | "Not Yet Concluded";
  operatingTestsDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  operatingTestsTextResponse?: { html: string; plainText: string };
  
  // Rollforward
  rollforwardFromDate: string;
  rollforwardToDate: string;
  rollforwardInquiries: RollforwardInquiry[];
  hasIcfrChanges: boolean;
  rollforwardOperatingTestsDocuments: Array<{
    id: string;
    type: 'textbox' | 'linked' | 'uploaded';
    name: string;
    reference?: string;
    url?: string;
    content?: string;
    textResponse?: { html: string; plainText: string };
  }>;
  rollforwardOperatingTestsTextResponse?: { html: string; plainText: string };
  icfrChanges: string;
  additionalProcedures: string;
  gitcOperatingEffectively: boolean;
  gitcDetails: string;
  isInquirySufficient: boolean;
  rollforwardSteps: OperatingEffectivenessStep[];
  rollforwardOperatingEffective: "Yes" | "No" | "Not Yet Concluded";
}

export const WORKSPACES = [
  "Inventory",
  "Revenue",
  "Fixed Assets",
  "Payroll",
  "Cash and Bank",
  "Accounts Receivable",
  "Accounts Payable",
  "Intangible Assets",
  "Investments",
  "Provisions",
];

export const FREQUENCY_OPTIONS = [
  "Annually",
  "Quarterly",
  "Monthly",
  "Weekly",
  "Daily",
  "As needed",
  "Many times per day",
];

export const NATURE_OF_PROCEDURE_OPTIONS = [
  "Inquiry + Observation",
  "Inquiry + Inspection",
  "Observation",
  "Inspection",
];

export const getDefaultControlData = (): InternalControlData => ({
  controlId: "IC-001",
  controlName: "",
  workspaceLinked: [],
  controlDescription: "",
  typeOfControl: "Direct",
  nature: "Manual",
  approach: "Preventive",
  controlTypes: {
    verification: false,
    authorizationsApprovals: false,
    physicalControlsCounts: false,
    controlsOverIUC: false,
    reconciliations: false,
    controlWithReviewElement: false,
    gitcControl: false,
  },
  addressesSignificantRisk: false,
  includesReviewElement: false,
  detailedDescriptionEnabled: false,
  steps: [],
  iucAssociations: [],
  iucDocuments: [],
  iucTextResponse: undefined,
  controlOutputs: [],
  outputDocuments: [],
  outputTextResponse: undefined,
  designImplementationSteps: [],
  testOfDesignImplementation: "",
  evidenceDocuments: [],
  evidenceDesignDocuments: [],
  evidenceDesignTextResponse: undefined,
  designFactor1Response: "",
  designFactor2Enabled: false,
  controlOwners: [],
  designFactor3Enabled: false,
  frequency: "",
  consistency: "",
  designFactor4Enabled: false,
  designFactor4Content: "",
  designFactor5Enabled: false,
  designFactor5Content: "",
  isDependentOnOtherControls: false,
  dependentControls: [],
  isDesignEffective: "Not Yet Concluded",
  isProperlyImplemented: "Not Yet Concluded",
  usePriorYearEvidence: false,
  changesInProcess: "",
  changesSignificant: false,
  testedCurrentYear: false,
  associatedControls: [],
  priorYearDocument: "",
  priorYearDocuments: [],
  priorYearTextResponse: undefined,
  riskAssociated: "Not Higher",
  riskBasisConclusion: "",
  timingOfProcedure: "Apportion",
  timingDocuments: [],
  timingTextResponse: undefined,
  frequencyOfPerformance: "",
  riskOfMaterialMisstatement: "",
  sampleSize: "",
  planningForDeviations: false,
  deviationDescription: "",
  deviationRationale: "",
  operatingSteps: [],
  operatingTestsDocuments: [],
  operatingTestsTextResponse: undefined,
  isOperatingEffective: "Not Yet Concluded",
  rollforwardFromDate: "",
  rollforwardToDate: "",
  rollforwardInquiries: [],
  hasIcfrChanges: false,
  rollforwardOperatingTestsDocuments: [],
  rollforwardOperatingTestsTextResponse: undefined,
  icfrChanges: "",
  additionalProcedures: "",
  gitcOperatingEffectively: false,
  gitcDetails: "",
  isInquirySufficient: true,
  rollforwardSteps: [],
  rollforwardOperatingEffective: "Not Yet Concluded",
  relatedAccounts: [],
});

